// Project detail — CTSK-146 (FR #299).
//
// First dedicated project page. Prior to this, clicking a project row
// deep-linked straight to /workitems?projectId=…; that link survives
// (the name cell still goes there). This page exists because session
// references need a read surface: the ratified FR #299 design puts a
// "Sessions" section on Project Detail, rendering each ref by derived
// kind (session_key → greet intro doc link when resolvable, Slack/URL
// → link out, opaque → display + copy).
//
// Kept deliberately lean: header (color/key/name/description/source
// URL), the work-item rollup chips the list page already computes, the
// Sessions section, and a jump to the filtered work-item list.
// Authoring stays in the Edit Project dialog (More fields — ctsk-137
// parity); this page is read-only.

import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import type { Project } from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from '../components/Toast.js';
import { SessionRefsSection } from '../components/SessionRefs.js';
import { EmptyState } from '../components/EmptyState.js';

export function ProjectDetail({
  id,
  onNavigate,
}: {
  id: string;
  onNavigate: (path: string) => void;
}) {
  const projectQ = useQuery({
    queryKey: ['project', id],
    queryFn: () => apiSingle<Project>(`/projects/${id}`),
  });

  if (projectQ.isLoading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading project…</p>;
  }
  const project = projectQ.data;
  if (!project) {
    return (
      <EmptyState
        title="Project not found"
        body="It may have been deleted, or it belongs to another organization."
      />
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 14,
              height: 14,
              borderRadius: 3,
              background: project.color ?? 'var(--accent-primary)',
            }}
          />
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 15 }}>{project.key}</code>
          <h1 style={{ margin: 0, fontSize: 20 }}>{project.name}</h1>
          {project.isArchived && (
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>archived</span>
          )}
        </div>
        {project.description && (
          <p style={{ color: 'var(--text-muted)', margin: '8px 0 0', maxWidth: 720 }}>
            {project.description}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 10,
            fontSize: 13,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <a
            href={`/workitems?projectId=${project.id}`}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(`/workitems?projectId=${project.id}`);
            }}
            style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
          >
            {project.openWorkItemCount} open / {project.workItemCount} work items →
          </a>
          {project.sourceUrl && (
            <a
              href={project.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={project.sourceUrl}
              style={{
                color: 'var(--accent-primary)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <ExternalLink size={13} strokeWidth={1.75} aria-hidden />
              Source
            </a>
          )}
          {project.key && (
            <button
              type="button"
              className="btn btn-sm"
              title={`Copy short URL: /pj/${project.key}`}
              onClick={async () => {
                const url = `${window.location.origin}/pj/${project.key}`;
                const ok = await copyToClipboard(url);
                if (ok) toast.success('Short URL copied');
                else toast.error('Could not copy — your browser blocked clipboard access');
              }}
              style={{
                fontSize: 12,
                padding: '2px 8px',
                background: 'transparent',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              🔗 Copy link
            </button>
          )}
        </div>
      </header>

      <section aria-labelledby="project-sessions-heading">
        {/* CTSK-281 — help affordance inline with the header (pattern:
            PatsPanel's "Learn more about roles →" link, CTSK-250). */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            margin: '0 0 8px',
            paddingBottom: 6,
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <h2 id="project-sessions-heading" style={{ fontSize: 14, margin: 0 }}>
            Sessions
          </h2>
          <a
            href="/help/project-sessions"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('/help/project-sessions');
            }}
            style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}
            title="What are session refs?"
          >
            Help ↗
          </a>
        </div>
        <SessionRefsSection refs={project.sessionRefs ?? []} onNavigate={onNavigate} />
      </section>
    </div>
  );
}
